import { store } from "../../redux/store";
import { selectUser } from "../../redux/Slice/userSlice";
import { selectActiveHotel } from "../../redux/Slice/hotelSlice";
import { isNumber } from "./typeUtils";

// Define the hotel permission interface
interface HotelPermission {
  hotelId: number;
  permission: number;
  notification?: boolean | null;
}
export const ACTIONS = {
  DEVELOPER: 60,
  MODIFY_GNHSTAFF: 50,
  ONBOARD_HOTEL: 50,
  MODIFY_HOTELSTAFF: 40,
  EDIT_CONFIG: 50,
  ADD_ROOM: 50,
  SWITCH_COMPSET: 40,
  NOTIFY_HOTEL: 40,
  VIEW_HOTELSTAFF: 40,
  UPDATE_LEFTTOSELL: 40,
  NEW_EXP: 5,
  MANAGE_PRO_HOTEL: 30,
  VIEW_CONFIG: 30,
  EDIT_COMPSET: 30,
  EDIT_ROOM: 30,
  EDIT_OWS: 10,
  PUBLISH: 10,
  YIELD_SHEET: 10,
  SUGGESTION: 5,
};

const getCurrentUserState = () => {
  const state = store.getState();
  return {
    ...selectUser(state),
    activeHotel: selectActiveHotel(state),
  };
};

/**
 * Checks if the user has the required permission level for a specific hotel
 * @param hotelId The hotel ID to check permissions for
 * @param requiredPermission The minimum permission level required
 * @returns Boolean indicating if the user has sufficient permissions
 */
export const hasPermission = (requiredPermission: number): boolean => {
  const { permissions, activeHotel } = getCurrentUserState();

  if (!activeHotel?.hotelId) {
    return false;
  }

  // Check for the new GNH permissions structure
  if (typeof permissions === "object" && "gnh" in permissions) {
    return permissions.gnh >= requiredPermission;
  }

  // Check for legacy number permissions
  if (isNumber(permissions)) {
    return permissions >= requiredPermission;
  }

  if (Array.isArray(permissions)) {
    const hotelPermission = permissions.find(
      (p: HotelPermission) => p.hotelId === activeHotel.hotelId,
    );
    if (hotelPermission) {
      return hotelPermission.permission >= requiredPermission;
    }
  }

  return false;
};

/**
 * Gets all hotels that the user has at least the specified permission level for
 * @param minPermission The minimum permission level required
 * @returns Array of hotel IDs that the user has sufficient permissions for
 */
export const getHotelsWithPermission = (minPermission: number): number[] => {
  const { permissions, hotels } = getCurrentUserState();

  if (typeof permissions === "number" && permissions >= minPermission) {
    return hotels && Array.isArray(hotels) ? hotels.map((h) => h.hotelId) : [];
  }

  if (hotels && Array.isArray(hotels)) {
    return hotels
      .filter((h) => h.permission >= minPermission)
      .map((h) => h.hotelId);
  }

  return [];
};
